import { useMemo } from 'react'
import { useSelectedDenaliScope } from '../../DenaliAnalysis/filters/DenaliScopeProvider'

/**
 * Derives a scopePredicate request object from a scope resolution.
 * Returns null when scope is inactive or unsupported.
 */
export function deriveScopePredicate (scopeResolution) {
  if (!scopeResolution?.supported) return null
  return { code: scopeResolution.code, parameters: scopeResolution.parameters }
}

/**
 * Resolves the active scope picker state against a purpose's scope predicates.
 *
 * @param {Array} scopePredicates - The purpose's scopePredicates array
 * @param {boolean} scopeEnabled - Whether the presentation has scope enabled
 * @returns {null|{supported: false, scopeType: string}|{supported: true, code: string, parameters: object}}
 *   null when scope is not enabled or no scope is active;
 *   { supported: false } when the active scope type has no matching predicate;
 *   { supported: true, code, parameters } when a predicate matches the active scope.
 */
export default function useBlueprintScopeResolution (scopePredicates, scopeEnabled) {
  const { scope, values } = useSelectedDenaliScope()

  return useMemo(() => {
    if (!scopeEnabled || !scope || !values?.length) return null

    const predicates = scopePredicates || []
    const match = predicates.find(sp => sp.scopeType === scope.name)

    if (!match) {
      return { supported: false, scopeType: scope.name }
    }

    const resolvedValues = match.type === 'expression' && values.length === 1
      ? values[0]
      : values
    const parameters = match.fromParameter
      ? { [match.fromParameter]: resolvedValues }
      : { from: resolvedValues }

    return {
      supported: true,
      code: match.code,
      parameters
    }
  }, [scopeEnabled, scope, values, scopePredicates])
}
